import { useState, useEffect } from 'react';
import { api } from '../lib/api';

export interface ContentItem {
  id: string;
  page_key?: string;
  page: string;
  section: string;
  type: string;
  content: string;
  image_url?: string;
  video_url?: string;
  button_text?: string;
  button_url?: string;
  order: number;
  metadata?: any;
  created_at?: string;
}

export interface SliderItem {
  id: string;
  page: string;
  title: string;
  subtitle: string;
  image_url?: string;
  bg_image_url?: string;
  button_text?: string;
  button_url?: string;
  order: number;
}

export function useContentManager() {
  const [content, setContent] = useState<ContentItem[]>([]);
  const [sliders, setSliders] = useState<SliderItem[]>([]);
  const [loading, setLoading] = useState(true);

  const loadContent = async () => {
    try {
      setLoading(true);
      
      // İçerikleri yükle
      const contentData = await api.get('/all-content');

      if (contentData && contentData.length > 0) {
        const mappedContent: ContentItem[] = contentData.map((item: any) => ({
          id: item.id,
          page_key: item.page_key,
          page: item.page,
          section: item.section,
          type: item.type,
          content: item.content,
          image_url: item.image_url,
          video_url: item.video_url,
          button_text: item.button_text,
          button_url: item.button_url,
          order: item.order,
          metadata: item.metadata,
          created_at: item.created_at
        }));
        setContent(mappedContent);
      } else {
        setContent([]);
      }

      // Slider'ları yükle
      const sliderData = await api.get('/sliders');

      if (sliderData) {
        setSliders(sliderData);
      }

    } catch (error) {
      console.error('Loading error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getContentItem = (page: string, section: string): ContentItem | undefined => {
    // content'in array olduğundan emin ol
    if (!Array.isArray(content)) {
      console.warn('Content is not an array:', content);
      return undefined;
    }

    // Match by page and section, or fallback to matching page_key
    const items = content.filter(item => 
      (item.page === page && item.section === section) ||
      (item.page_key && (item.page_key === `${page}-${section}` || item.page_key.startsWith(`${page}-${section}-`)))
    );
    if (items.length === 0) return undefined;
    
    // En yeni kaydı bul (created_at veya id'ye göre)
    const sortedItems = items.sort((a, b) => {
      // Önce created_at'e göre sırala
      if (a.created_at && b.created_at) {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
      // created_at yoksa id'ye göre sırala (UUID'ler için)
      return b.id.localeCompare(a.id);
    });
    
    return sortedItems[0];
  };

  const updateContentItem = async (item: ContentItem) => {
    try {
      // Geçici ID kontrolü - temp- ile başlıyorsa yeni kayıt
      const isNewItem = !item.id || item.id.startsWith('temp-');
      
      if (!isNewItem) {
        // Mevcut öğeyi güncelle
        await api.put(`/content/${item.id}`, {
          page: item.page,
          section: item.section,
          type: item.type,
          content: item.content,
          image_url: item.image_url,
          video_url: item.video_url,
          button_text: item.button_text,
          button_url: item.button_url,
          order: item.order,
          metadata: item.metadata
        });
      } else {
        const ts = Date.now().toString();
        const suffix = `-${ts.slice(-6)}`;
        const base = `${item.page}-${item.section}`;
        const safePageKey = (base + suffix).length > 50 ? base.slice(0, 50 - suffix.length) + suffix : base + suffix;
        const safeTitle = (item.section || 'Untitled').slice(0, 50);
        await api.post('/content', {
          title: safeTitle,
          page_key: safePageKey,
          page: item.page,
          section: item.section,
          type: item.type,
          content: item.content,
          image_url: item.image_url,
          video_url: item.video_url,
          button_text: item.button_text,
          button_url: item.button_url,
          order: item.order,
          metadata: item.metadata
        });
      }

      await loadContent();
    } catch (error) {
      console.error('Update error:', error);
      throw error;
    }
  };

  const deleteContentItem = async (id: string) => {
    try {
      await api.delete(`/content/${id}`);
      await loadContent();
    } catch (error) {
      console.error('Delete error:', error);
      throw error;
    }
  };

  const updateSliderItem = async (item: SliderItem) => {
    try {
      // Yeni slider mı kontrol et (geçici ID içeriyorsa veya ID yoksa)
      const isNewSlider = !item.id || item.id.includes('home-projects-');
      
      if (isNewSlider) {
        // Yeni slider için benzersiz ID oluştur
        const newId = `slider-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        await api.post('/sliders', {
          id: newId,
          title: item.title,
          subtitle: item.subtitle,
          image_url: item.image_url,
          order: item.order
        });
      } else {
        // Mevcut slider'ı güncelle
        await api.put(`/sliders/${item.id}`, {
          title: item.title,
          subtitle: item.subtitle,
          image_url: item.image_url,
          order: item.order
        });
      }

      await loadContent();
    } catch (error) {
      console.error('Slider update error:', error);
      throw error;
    }
  };

  const deleteSliderItem = async (id: string) => {
    try {
      await api.delete(`/sliders/${id}`);
      await loadContent();
    } catch (error) {
      console.error('Slider delete error:', error);
      throw error;
    }
  };

  const reloadContent = async () => {
    await loadContent();
  };

  useEffect(() => {
    loadContent();
  }, []);

  return {
    content,
    sliders,
    loading,
    getContentItem,
    updateContentItem,
    deleteContentItem,
    updateSliderItem,
    deleteSliderItem,
    reloadContent
  };
}
